import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Save, Mail, User, Moon, Sun, Monitor } from 'lucide-react';

export default function SettingsPage() {
  const [senderEmail, setSenderEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { theme, setTheme } = useTheme();

  useEffect(() => {
    api.get('/settings')
      .then(res => {
        setSenderEmail(res.data.sender_email || '');
        setSenderName(res.data.sender_name || '');
      })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!senderEmail) {
      toast.error('Sender email is required');
      return;
    }
    setSaving(true);
    try {
      await api.put('/settings', { sender_email: senderEmail, sender_name: senderName });
      toast.success('Email settings updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading settings...</div>;

  return (
    <div data-testid="settings-page">
      <div className="mb-6">
        <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Settings</h2>
        <p className="text-sm text-muted-foreground font-body mt-1">Configure email and application settings</p>
      </div>

      {/* Appearance Settings */}
      <div className="border border-border bg-card rounded-sm p-6 max-w-lg mb-6">
        <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
          {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          Appearance
        </h3>
        <p className="text-xs text-muted-foreground mb-5">Customize how the application looks on your device.</p>

        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-border">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-sm ${theme === 'light' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                <Sun className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Light Mode</p>
                <p className="text-xs text-muted-foreground">Clean and bright interface</p>
              </div>
            </div>
            <Switch 
              checked={theme === 'light'} 
              onCheckedChange={() => setTheme('light')}
              data-testid="theme-light-switch"
            />
          </div>

          <div className="flex items-center justify-between py-3 border-b border-border">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-sm ${theme === 'dark' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                <Moon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">Dark Mode</p>
                <p className="text-xs text-muted-foreground">Easy on the eyes in low light</p>
              </div>
            </div>
            <Switch 
              checked={theme === 'dark'} 
              onCheckedChange={() => setTheme('dark')}
              data-testid="theme-dark-switch"
            />
          </div>

          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-sm bg-muted">
                <Monitor className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">System Default</p>
                <p className="text-xs text-muted-foreground">Follows your device settings</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
                setTheme(systemTheme);
                toast.success(`Switched to system theme (${systemTheme})`);
              }}
              className="text-xs rounded-sm"
              data-testid="theme-system-btn"
            >
              Use System
            </Button>
          </div>
        </div>
      </div>

      {/* Email Configuration */}
      <div className="border border-border bg-card rounded-sm p-6 max-w-lg">
        <h3 className="font-heading font-semibold text-foreground mb-4">Email Configuration</h3>
        <p className="text-xs text-muted-foreground mb-5">These settings control how payslip emails are sent to employees.</p>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Sender Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <Input
                value={senderName}
                onChange={e => setSenderName(e.target.value)}
                placeholder="HireHub Payroll"
                className="pl-10 rounded-sm"
                data-testid="sender-name-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Sender Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <Input
                value={senderEmail}
                onChange={e => setSenderEmail(e.target.value)}
                placeholder="payroll@hirehubitsolutions.com"
                type="email"
                className="pl-10 rounded-sm"
                data-testid="sender-email-input"
              />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Use <span className="font-mono">onboarding@resend.dev</span> for testing, or your verified domain email for production.
            </p>
          </div>

          <div className="pt-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-primary hover:bg-primary/90 rounded-sm"
              data-testid="save-settings-btn"
            >
              <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm p-6 max-w-lg mt-6">
        <h3 className="font-heading font-semibold text-foreground mb-2">Domain Verification</h3>
        <p className="text-sm text-muted-foreground mb-3">
          To send payslip emails to all employees, you need to verify your domain with Resend.
        </p>
        <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside font-body">
          <li>Go to <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-primary underline hover:text-primary/80">resend.com/domains</a></li>
          <li>Add your domain (e.g., <span className="font-mono text-xs">hirehubitsolutions.com</span>)</li>
          <li>Add the DNS records Resend provides to your domain</li>
          <li>Once verified, update the sender email above to use your domain</li>
        </ol>
      </div>
    </div>
  );
}
